import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { Link } from 'react-router-dom';
import { products, categories, siteImages, CONTACT_INFO } from '../data/mock';
import { CheckCircle2, ArrowRight, Truck, ShieldCheck, Clock, RotateCcw, Star, Zap } from 'lucide-react';

const HomePage = () => {
  const featuredProduct = products.find(p => p.slug === 'elite-all-in-one-vr-headset') || products[0];
  const gridSlugs = [
    'touch-control-true-wireless-earbuds',
    'azure-ice-mechanical-keyboard',
    'wireless-bluetooth-gaming-mouse',
    'modern-wireless-headphones',
    'smart-bluetooth-speaker-led',
    'wireless-noise-cancelling-headphones',
    'ultra-wide-curved-computer-monitor',
    'graphic-tablet-monitor-stylus',
  ];
  const productsGrid = gridSlugs.map(s => products.find(p => p.slug === s)).filter(Boolean);

  useEffect(() => {
    document.title = 'Cloud Core Gadgets | Smart Electronics & Everyday Tech';
  }, []);

  return (
    <div className="min-h-screen bg-white text-dark-800">
      <Header />
      <CartDrawer />

      <main>
        {/* Hero — split layout */}
        <section className="relative bg-dark-900 overflow-hidden">
          <div className="absolute inset-0">
            <img src={siteImages.heroMain} alt="" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-r from-dark-900 via-dark-900/90 to-dark-900/40" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 border border-white/15 text-athletic-300 text-xs font-bold uppercase tracking-[0.2em] rounded-full mb-6">
                <Zap className="w-3.5 h-3.5" /> Fresh tech, curated
              </span>
              <h1 className="font-display text-4xl md:text-6xl font-bold text-white tracking-tight leading-[1.05]">
                Smarter gadgets for the way you <span className="text-transparent bg-clip-text bg-gradient-to-r from-athletic-400 to-power-400">live and create</span>
              </h1>
              <p className="mt-5 text-lg text-dark-200 max-w-xl">
                From immersive audio to pro creator displays, Cloud Core Gadgets brings a handpicked lineup of everyday tech &mdash; shipped free across the USA.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/shop" className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-athletic-500 to-power-600 px-7 py-3.5 font-semibold text-white transition hover:opacity-90 shadow-button">
                  Shop Gadgets <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/shop?category=audio-sound" className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-7 py-3.5 font-semibold text-white hover:bg-white/10 transition">
                  Explore Audio
                </Link>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-dark-300">
                <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-athletic-400" /> Free USA shipping</span>
                <span className="flex items-center gap-2"><RotateCcw className="w-4 h-4 text-athletic-400" /> 30-day returns</span>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-elevated">
                <img src={siteImages.heroSecondary} alt="Premium audio gear" className="w-full h-[420px] object-cover" />
                <div className="absolute bottom-4 left-4 right-4 bg-dark-900/80 backdrop-blur rounded-2xl p-4 flex items-center justify-between">
                  <div>
                    <p className="text-white font-semibold">{featuredProduct.name}</p>
                    <p className="text-athletic-400 text-sm">Flagship pick</p>
                  </div>
                  <span className="text-white font-bold text-xl">${featuredProduct.price.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Categories quick nav */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-dark-900">Shop by category</h2>
                <p className="mt-2 text-dark-500">Find your next upgrade across our curated collections.</p>
              </div>
              <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-athletic-700 hover:gap-2.5 transition-all">
                View all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/shop?category=${category.slug}`}
                  className="group relative h-52 rounded-2xl overflow-hidden shadow-sm hover:shadow-card-hover transition-all bg-dark-900"
                >
                  {category.image && (
                    <img src={category.image} alt={category.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-110 transition-all duration-500" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-950 via-dark-950/40 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-4">
                    <h3 className="font-display text-lg font-bold text-white">{category.name}</h3>
                    <p className="text-xs text-athletic-300 mt-0.5">{category.productCount} product{category.productCount > 1 ? 's' : ''}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured products grid */}
        <section className="bg-dark-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-wider text-athletic-700 mb-2">Trending now</span>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-dark-900">Best sellers this week</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {productsGrid.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            <div className="text-center mt-12">
              <Link to="/shop" className="inline-flex items-center gap-2 px-6 py-3 bg-dark-900 text-white font-semibold rounded-lg hover:bg-dark-800 transition">
                View all products <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Spotlight banner */}
        <section className="relative overflow-hidden bg-dark-900">
          <img src={siteImages.ctaBackground} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark-900 to-dark-900/50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-10 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-block text-xs font-bold uppercase tracking-wider text-athletic-400 mb-3">Flagship spotlight</span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3">{featuredProduct.name}</h2>
              <p className="text-dark-200 mb-5 max-w-lg">{featuredProduct.description.slice(0, 180)}...</p>
              <p className="text-3xl font-bold text-white mb-6">${featuredProduct.price.toFixed(2)}</p>
              <div className="flex flex-wrap gap-3">
                <Link to={`/product/${featuredProduct.slug}`} className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-athletic-500 to-power-600 text-white rounded-lg font-semibold hover:opacity-90 transition">Buy now</Link>
                <Link to="/shop" className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white rounded-lg font-medium hover:bg-white/10 transition">Browse more</Link>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img src={featuredProduct.image} alt={featuredProduct.name} className="w-full max-w-md mx-auto rounded-3xl bg-white/5 p-6 ring-1 ring-white/10" />
            </div>
          </div>
        </section>

        {/* Why Cloud Core */}
        <section className="bg-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
              <img className="h-80 w-full lg:h-[420px] lg:max-w-md rounded-3xl object-cover shadow-elevated flex-shrink-0" src={siteImages.heroLifestyle} alt="Why Cloud Core Gadgets" />
              <div>
                <span className="inline-block px-3 py-1 bg-athletic-50 text-athletic-700 text-xs font-bold uppercase tracking-wider rounded-full mb-3">Why Cloud Core</span>
                <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-dark-900">
                  Tech worth <span className="text-athletic-600">owning</span>
                </h2>
                <p className="mt-5 text-lg text-dark-500">
                  We skip the endless shelves of lookalikes and hand-pick a small lineup of gadgets we would actually buy &mdash; then back every one with free shipping and easy returns.
                </p>
                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {['Handpicked, tested gear', 'Crystal-clear audio', 'Sharp, color-true displays', 'Responsive gaming controls', 'Fast free USA shipping', 'Honest, upfront pricing'].map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-dark-700">
                      <CheckCircle2 className="h-5 w-5 flex-none text-athletic-600 mt-0.5" /> <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-10">
                  <Link to="/why-choose-us" className="text-sm font-semibold text-athletic-700 hover:text-athletic-800">Learn what sets us apart <span aria-hidden="true">&rarr;</span></Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="bg-dark-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-dark-900">Loved by our customers</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: 'Marcus T.', text: 'The wireless headphones sound incredible and shipping was quicker than I expected. Exactly what I hoped for.' },
                { name: 'Priya S.', text: 'Ordered the mechanical keyboard and the drawing tablet — both feel premium. Great little shop with real picks.' },
                { name: 'Devon R.', text: 'Support answered my question in minutes and the return process was effortless. I\u2019ll be back for more gear.' },
              ].map((r, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-dark-100 shadow-sm">
                  <div className="flex gap-1 mb-3 text-athletic-500">
                    {[...Array(5)].map((_, s) => <Star key={s} className="w-4 h-4 fill-current" />)}
                  </div>
                  <p className="text-dark-700 mb-4">&ldquo;{r.text}&rdquo;</p>
                  <p className="font-semibold text-dark-900 text-sm">{r.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-3xl font-bold text-dark-900 text-center mb-10">Frequently asked questions</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[
                { q: 'What is the typical shipping time?', a: `Your order is processed and shipped via USPS within 1 business day. Delivery usually lands within 2 to 4 business days. For delays, contact us at ${CONTACT_INFO.phone}.` },
                { q: 'Is shipping free?', a: 'Yes — every order ships with free USPS Standard Shipping within the USA, no minimum required.' },
                { q: 'How do returns work?', a: 'You have 30 days from delivery to start a return. Just reach out to our team and we will guide you through it.' },
                { q: 'How do I reach support?', a: `Our team is available at ${CONTACT_INFO.phone} or by email at ${CONTACT_INFO.email}.` },
              ].map((faq, i) => (
                <div key={i} className="bg-dark-50 rounded-2xl p-6 border border-dark-100">
                  <h3 className="font-display text-lg font-semibold text-dark-900">{faq.q}</h3>
                  <p className="mt-2 text-dark-600">{faq.a}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/faq" className="inline-flex items-center gap-2 text-athletic-700 hover:text-athletic-800 font-semibold">See all FAQs <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
